<template>
  <div>
    <header class="text-center">
      <div class="flex flex-wrap justify-center hover:shadow-xl hero-section">
        <div class="flex w-full h-full md:w lg:w-1/2 px-4">
          <div class="p-8 text-center hover:bg-white rounded-md transition duration-200 my-auto">
            <h1 class="display-4 text-5xl md:text-5xl font-bold">
              Unlock the Power of Sensor Data with Advanced Cloud Analysis Tools
            </h1>
            <p class="lead my-4 text-lg md:text-xl text-coolGray-500 font-medium">
              Leverage cloud collaboration to import raw data, integrate IoT devices, and generate comprehensive reports with ease.
            </p>
            <router-link
              class="btn btn-primary btn-lg mt-5 mb-2"
              to="/register/freemium"
              role="button"
            >
              Try for Free
            </router-link>
            <p class="text-sm text-coolGray-400 mt-0">
              Need live sensor dashboards?
              <a
                href="https://djuli-real-time-353283799832.europe-west4.run.app/"
                target="_blank"
                rel="noopener noreferrer"
                class="text-green-500 hover:text-green-600"
              >Try djuli real-time &rarr;</a>
            </p>
          </div>
        </div>
        <div class="w-full md:w lg:w-1/2 px-4 flex items-center justify-center">
          <img
            src="../assets/Images/background_image.png"
            class="img-fluid"
            alt="Cloud Analytics"
          >
        </div>
      </div>
    </header>
        
    <div
      :style="divStyle"
      class="scroll-div container p-4 mx-auto section-features"
    >
      <div class="md:max-w-4xl mx-auto mb-8 md:mb-10 text-center">
        <h1 class="my-4 text-3xl md:text-4xl leading-tight text-coolGray-900 font-bold tracking-tighter">
          Revolutionize Your Data Analysis
        </h1>
        <p class="text-lg md:text-xl text-coolGray-500 font-medium">
          With our integrated real time data analysis, collaboration and sharing capabilities, you can manage every aspect of your R&amp;D in a secure cloud.
        </p>
      </div>
      <div class="flex flex-wrap justify-center -mx-4">
        <div class="w-full md:w-1/2 lg:w-1/3 px-4">
          <div class="h-full p-8 text-center hover:bg-white rounded-md hover:shadow-xl transition duration-200">
            <div class="inline-flex h-28 w-28 mb-7 mx-auto items-center justify-center rounded-lg">
              <img
                src="../assets/Images/icon-cloud.png"
                class="img-fluid"
                alt="Cloud Analytics"
              >
            </div>
            <h3 class="mb-4 text-xl md:text-2xl text-green-600 leading-tight font-bold">
              Collaborative & Secure
            </h3>
            <p class="text-coolGray-500 font-medium">
              Scalable, secure, and consistent data for seamless collaboration
            </p>
            <div class="inline-flex h-28 w-28 mx-auto items-center justify-center rounded-lg">
              <a
                class="text-green-500"
                href="#"
                @click.prevent="scrollToSection('secure-section')"
              >Learn more...</a>
            </div>
          </div>
        </div>
        <div class="w-full md:w-1/2 lg:w-1/3 px-4">
          <div class="h-full p-8 text-center hover:bg-white rounded-md hover:shadow-xl transition duration-200">
            <div class="inline-flex h-28 w-28 mb-7 mx-auto items-center justify-center rounded-lg">
              <img
                src="../assets/Images/icon-config.png"
                class="img-fluid"
                alt="Cloud Analytics"
              >
            </div>
            <h3 class="mb-4 text-xl md:text-2xl text-green-600 leading-tight font-bold">
              Report Generation
            </h3>
            <p class="text-coolGray-500 font-medium">
              Import raw data and create detailed reports with just a few clicks
            </p>
            <div class="inline-flex h-28 w-28 mx-auto items-center justify-center rounded-lg">
              <a
                class="text-green-500"
                href="#"
                @click.prevent="scrollToSection('report-generation-section')"
              >Learn more...</a>
            </div>
          </div>
        </div>
        <div class="w-full md:w-1/2 lg:w-1/3 px-4">
          <div class="h-full p-8 text-center hover:bg-white rounded-md hover:shadow-xl transition duration-200">
            <div class="inline-flex h-28 w-28 mb-7 mx-auto items-center justify-center rounded-lg">
              <img
                src="../assets/Images/icon-chip.png"
                class="img-fluid"
                alt="Cloud Analytics"
              >
            </div>
            <h3 class="mb-4 text-xl md:text-2xl text-green-600 leading-tight font-bold">
              API Connectivity
            </h3>
            <p class="text-coolGray-500 font-medium">
              Connect external applications and IoT devices effortlessly
            </p>
            <div class="inline-flex h-28 w-28 mx-auto items-center justify-center rounded-lg">
              <a
                class="text-green-500"
                href="#"
                @click.prevent="scrollToSection('connectivity-section')"
              >Learn more...</a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="container mx-auto section-quotes -mt-10">
      <div class="md:max-w-4xl mx-auto pb-8 pt-10 md:mb-10 text-center">
        <h2 class="mt-4 mb-2 text-4xl md:text-5xl leading-tight text-coolGray-900 tracking-tighter">
          What scientists <span class="header-quotes-effect font-bold">say</span>
        </h2>
        <div class="line-header-quote" />
      </div>
      <div
        ref="quotesSection"
        class="flex flex-wrap justify-center px-4"
      >
        <div class="w-full md:w-1/2 lg:w-1/3 pb-10 px-4">
          <div
            class="quote-card quote-card--white quote-from-left"
            :class="{ 'quote-visible': quotesVisible }"
          >
            <p class="quote-text">
              &ldquo;It helped me go from theory to real-world sensor data analysis in a simple, interactive way&rdquo;
            </p>
            <footer class="quote-footer">
              <span class="quote-dash">&mdash;</span>
              <span class="quote-author">Jose Montoya - Universitetet i Sørøst-Norge</span>
            </footer>
          </div>
        </div>
        <div class="w-full md:w-1/2 lg:w-1/3 pb-10 px-4">
          <div
            class="quote-card quote-card--green quote-from-bottom quote-delay-1"
            :class="{ 'quote-visible': quotesVisible }"
          >
            <p class="quote-text">
              &ldquo;We now have full traceability, down to every data point!&rdquo;
            </p>
            <footer class="quote-footer">
              <span class="quote-dash">&mdash;</span>
              <span class="quote-author">Martin Peacock - Zimmer and Peacock</span>
            </footer>
          </div>
        </div>
        <div class="w-full md:w-1/2 lg:w-1/3 pb-10 px-4">
          <div
            class="quote-card quote-card--white quote-from-right quote-delay-2"
            :class="{ 'quote-visible': quotesVisible }"
          >
            <p class="quote-text">
              &ldquo;It takes away all the manual data management tasks and allows me to focus 100% at the science&rdquo;
            </p>
            <footer class="quote-footer">
              <span class="quote-dash">&mdash;</span>
              <span class="quote-author">Sindre Søpstad - Zimmer and Peacock </span>
            </footer>
          </div>
        </div>
      </div>
    </div>
    <div class="container mx-auto">
      <div
        id="secure-section"
        class="flex flex-wrap justify-center p-4 features-section-card"
      >
        <div class="w-full lg:w-1/2 text-left pb-5 my-auto relative">
          <span class="inline-block py-px px-2 mb-4 text-xs leading-5 text-green-500 bg-green-100 font-medium uppercase rounded-full shadow-sm">
            Stability
          </span>
          <div class="md:w-full lg:w-3/4">
            <h3 class="text-3xl md:text-4xl leading-tight text-coolGray-900 tracking-tighter font-bold">
              Scalable and Secure Solutions
            </h3>
            <p class="pt-4">
              Scale your storage and processing power as needed. Enjoy advanced security measures and regular updates to protect your data.
            </p>
          </div>
        </div>
        <div class="w-full lg:w-1/2 pb-5">
          <img
            src="../assets/Images/secure-scalable.png"
            class="ml-auto"
            alt="..."
          >
        </div>
      </div>
      <div
        id="report-generation-section"
        ref="djuliReportGifContainer"
        class="flex flex-wrap justify-center p-4 features-section-card"
      >
        <div class="w-full lg:w-1/2 pb-5">
          <img
            v-if="djuliReportGifVisible"
            src="../assets/Images/djuli-report-easy.gif"
            class=""
            alt="..."
          >
          <img
            v-else
            src="../assets/Images/djuli-report-easy.png"
            class=""
            alt="..."
          >
        </div>
        <div class="w-full lg:w-1/2 md:text-left lg:text-right pb-5 my-auto relative">
          <span class="inline-block py-px px-2 mb-4 text-xs leading-5 text-green-500 bg-green-100 font-medium uppercase rounded-full shadow-sm">
            Simplicity
          </span>
          <div class="md:w-full lg:w-3/4 lg:ml-auto">
            <h3 class="text-3xl md:text-4xl leading-tight text-coolGray-900 tracking-tighter font-bold">
              Create a professional report of your analysis in few clicks
            </h3>
            <p class="pt-4">
              Generate detailed reports effortlessly. Our intuitive interface guides you through each step, making the process quick and seamless.
            </p>
          </div>
        </div>
      </div>
      <div
        ref="djuliPreprocessingGifContainer"
        class="flex flex-wrap justify-center p-4 features-section-card"
      >
        <div class="w-full lg:w-1/2 text-left pb-5 my-auto relative">
          <span class="inline-block py-px px-2 mb-4 text-xs leading-5 text-green-500 bg-green-100 font-medium uppercase rounded-full shadow-sm">
            Accuracy
          </span>
          <div class="md:w-full lg:w-3/4">
            <h3 class="text-3xl md:text-4xl leading-tight text-coolGray-900 tracking-tighter font-bold">
              Utilize various analysis and preprocessing tools for deeper insights
            </h3>
            <p class="pt-4">
              Leverage a range of advanced analysis and preprocessing tools. Gain deeper insights and make informed decisions with precision.
            </p>
          </div>
        </div>
        <div class="w-full lg:w-1/2 pb-5">
          <img
            v-if="djuliPreprocessingGifVisible"
            src="../assets/Images/preprocessing-data.gif"
            class=""
            alt="..."
          >
          <img
            v-else
            src="../assets/Images/preprocessing-data.png"
            class=""
            alt="..."
          >
        </div>
      </div>
      <div class="flex flex-wrap justify-center p-4 features-section-card">
        <div class="w-full lg:w-1/2 pb-5 flex items-center justify-center">
          <div
            ref="formatsSection"
            class="formats-grid"
            :class="{ 'formats-visible': formatsVisible }"
          >
            <div class="format-tile csv">
              <div class="format-tile-icon">
                <svg
                  width="54"
                  height="54"
                  viewBox="0 0 54 54"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="8"
                    y="4"
                    width="30"
                    height="38"
                    rx="3"
                    fill="none"
                    stroke="#2d7a3a"
                    stroke-width="2.2"
                  />
                  <polyline
                    points="30,4 38,4 38,12 30,12 30,4"
                    fill="#2d7a3a"
                  />
                  <rect
                    x="4"
                    y="32"
                    width="28"
                    height="14"
                    rx="7"
                    fill="#1a7a30"
                  />
                  <text
                    x="18"
                    y="43"
                    text-anchor="middle"
                    font-size="9"
                    font-weight="800"
                    fill="white"
                    font-family="monospace"
                  >CSV</text>
                </svg>
              </div>
              <div class="format-tile-label">
                CSV
              </div>
              <div class="format-tile-sub">
                Comma Separated
              </div>
            </div>
            <div class="format-tile xml">
              <div class="format-tile-icon">
                <svg
                  width="54"
                  height="54"
                  viewBox="0 0 54 54"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="8"
                    y="4"
                    width="30"
                    height="38"
                    rx="3"
                    fill="none"
                    stroke="#e09000"
                    stroke-width="2.2"
                  />
                  <polyline
                    points="30,4 38,4 38,12 30,12 30,4"
                    fill="#e09000"
                  />
                  <text
                    x="23"
                    y="28"
                    text-anchor="middle"
                    font-size="13"
                    font-weight="800"
                    fill="#e09000"
                    font-family="monospace"
                  >&lt;/&gt;</text>
                  <rect
                    x="4"
                    y="32"
                    width="28"
                    height="14"
                    rx="7"
                    fill="#c07800"
                  />
                  <text
                    x="18"
                    y="43"
                    text-anchor="middle"
                    font-size="9"
                    font-weight="800"
                    fill="white"
                    font-family="monospace"
                  >XML</text>
                </svg>
              </div>
              <div class="format-tile-label">
                XML
              </div>
              <div class="format-tile-sub">
                Extensible Markup
              </div>
            </div>
            <div class="format-tile fhtml">
              <div class="format-tile-icon">
                <svg
                  width="54"
                  height="54"
                  viewBox="0 0 54 54"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="8"
                    y="4"
                    width="30"
                    height="38"
                    rx="3"
                    fill="none"
                    stroke="#e05000"
                    stroke-width="2.2"
                  />
                  <polyline
                    points="30,4 38,4 38,12 30,12 30,4"
                    fill="#e05000"
                  />
                  <line
                    x1="14"
                    y1="20"
                    x2="32"
                    y2="20"
                    stroke="#e05000"
                    stroke-width="2"
                  />
                  <line
                    x1="14"
                    y1="27"
                    x2="28"
                    y2="27"
                    stroke="#e05000"
                    stroke-width="2"
                  />
                  <rect
                    x="2"
                    y="32"
                    width="32"
                    height="14"
                    rx="4"
                    fill="#cc4000"
                  />
                  <text
                    x="18"
                    y="43"
                    text-anchor="middle"
                    font-size="8"
                    font-weight="800"
                    fill="white"
                    font-family="monospace"
                  >HTML</text>
                </svg>
              </div>
              <div class="format-tile-label">
                HTML
              </div>
              <div class="format-tile-sub">
                Web Markup
              </div>
            </div>
            <div class="format-tile xlsx">
              <div class="format-tile-icon">
                <svg
                  width="54"
                  height="54"
                  viewBox="0 0 54 54"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="4"
                    y="4"
                    width="28"
                    height="34"
                    rx="3"
                    fill="#1a7a40"
                  />
                  <text
                    x="18"
                    y="27"
                    text-anchor="middle"
                    font-size="22"
                    font-weight="900"
                    fill="white"
                    font-family="sans-serif"
                  >X</text>
                  <rect
                    x="30"
                    y="16"
                    width="20"
                    height="22"
                    rx="2"
                    fill="none"
                    stroke="#1a7a40"
                    stroke-width="1.5"
                  />
                  <line
                    x1="30"
                    y1="22"
                    x2="50"
                    y2="22"
                    stroke="#1a7a40"
                    stroke-width="1"
                  />
                  <line
                    x1="30"
                    y1="28"
                    x2="50"
                    y2="28"
                    stroke="#1a7a40"
                    stroke-width="1"
                  />
                  <line
                    x1="30"
                    y1="34"
                    x2="50"
                    y2="34"
                    stroke="#1a7a40"
                    stroke-width="1"
                  />
                  <line
                    x1="40"
                    y1="16"
                    x2="40"
                    y2="38"
                    stroke="#1a7a40"
                    stroke-width="1"
                  />
                </svg>
              </div>
              <div class="format-tile-label">
                XLSX
              </div>
              <div class="format-tile-sub">
                Excel Spreadsheet
              </div>
            </div>
            <div class="format-tile pss">
              <div class="format-tile-icon">
                <svg
                  width="54"
                  height="54"
                  viewBox="0 0 54 54"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <polygon
                    points="10,4 36,4 44,12 44,46 10,46"
                    fill="none"
                    stroke="#3a7ad4"
                    stroke-width="2"
                  />
                  <polygon
                    points="36,4 44,12 36,12"
                    fill="#3a7ad4"
                  />
                  <text
                    x="27"
                    y="32"
                    text-anchor="middle"
                    font-size="11"
                    font-weight="800"
                    fill="#3a7ad4"
                    font-family="monospace"
                  >.PSS</text>
                </svg>
              </div>
              <div class="format-tile-label">
                .PSS
              </div>
              <div class="format-tile-sub">
                Power Study File
              </div>
            </div>
            <div class="format-tile dt6">
              <div class="format-tile-icon">
                <svg
                  width="54"
                  height="54"
                  viewBox="0 0 54 54"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="8"
                    y="4"
                    width="38"
                    height="46"
                    rx="4"
                    fill="#d0d0d0"
                  />
                  <rect
                    x="8"
                    y="4"
                    width="38"
                    height="18"
                    rx="4"
                    fill="#888"
                  />
                  <text
                    x="27"
                    y="18"
                    text-anchor="middle"
                    font-size="10"
                    font-weight="800"
                    fill="white"
                    font-family="monospace"
                  >DT6</text>
                </svg>
              </div>
              <div class="format-tile-label">
                DT6
              </div>
              <div class="format-tile-sub">
                Telemetry Data
              </div>
            </div>
            <div class="format-tile fpng">
              <div class="format-tile-icon">
                <svg
                  width="54"
                  height="54"
                  viewBox="0 0 54 54"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="6"
                    y="4"
                    width="36"
                    height="44"
                    rx="4"
                    fill="#c5d8f8"
                  />
                  <polygon
                    points="38,4 42,4 42,8 38,8"
                    fill="#a0bce8"
                  />
                  <polygon
                    points="6,38 18,24 26,32 34,20 42,38"
                    fill="#3a6ad4"
                  />
                  <circle
                    cx="34"
                    cy="20"
                    r="5"
                    fill="#6090e8"
                  />
                </svg>
              </div>
              <div class="format-tile-label">
                PNG
              </div>
              <div class="format-tile-sub">
                Image File
              </div>
            </div>
            <div class="format-tile txt">
              <div class="format-tile-icon">
                <svg
                  width="54"
                  height="54"
                  viewBox="0 0 54 54"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <ellipse
                    cx="27"
                    cy="20"
                    rx="16"
                    ry="6"
                    fill="#0a5a6a"
                  />
                  <rect
                    x="11"
                    y="20"
                    width="32"
                    height="16"
                    fill="#084858"
                  />
                  <ellipse
                    cx="27"
                    cy="36"
                    rx="16"
                    ry="6"
                    fill="#0a5a6a"
                  />
                  <ellipse
                    cx="27"
                    cy="28"
                    rx="16"
                    ry="6"
                    fill="#0c7080"
                  />
                  <line
                    x1="20"
                    y1="24"
                    x2="34"
                    y2="24"
                    stroke="#a0d8e8"
                    stroke-width="1.2"
                  />
                  <line
                    x1="20"
                    y1="28"
                    x2="34"
                    y2="28"
                    stroke="#a0d8e8"
                    stroke-width="1.2"
                  />
                  <line
                    x1="20"
                    y1="32"
                    x2="34"
                    y2="32"
                    stroke="#a0d8e8"
                    stroke-width="1.2"
                  />
                </svg>
              </div>
              <div class="format-tile-label">
                TXT / DB
              </div>
              <div class="format-tile-sub">
                Text / Database
              </div>
            </div>
            <div class="format-tile aoi">
              <div class="format-tile-icon">
                <svg
                  width="54"
                  height="54"
                  viewBox="0 0 54 54"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <polyline
                    points="6,44 16,30 24,36 34,20 44,24 50,12"
                    stroke="#3aaa50"
                    stroke-width="2.2"
                    fill="none"
                  />
                  <polygon
                    points="6,44 16,30 24,36 34,20 44,24 50,12 50,44"
                    fill="#2d9a40"
                    opacity="0.35"
                  />
                  <polyline
                    points="6,44 16,36 24,40 34,30 44,34 50,24"
                    stroke="white"
                    stroke-width="1.5"
                    fill="none"
                  />
                  <polygon
                    points="6,44 16,36 24,40 34,30 44,34 50,24 50,44"
                    fill="#1a6a28"
                    opacity="0.5"
                  />
                  <line
                    x1="6"
                    y1="8"
                    x2="6"
                    y2="44"
                    stroke="#3aaa50"
                    stroke-width="2"
                  />
                  <line
                    x1="6"
                    y1="44"
                    x2="50"
                    y2="44"
                    stroke="#3aaa50"
                    stroke-width="2"
                  />
                </svg>
              </div>
              <div class="format-tile-label">
                AOI
              </div>
              <div class="format-tile-sub">
                Area of Interest
              </div>
            </div>
            <div class="format-tile ps">
              <div class="format-tile-icon">
                <svg
                  width="54"
                  height="54"
                  viewBox="0 0 54 54"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="8"
                    y="32"
                    width="10"
                    height="16"
                    rx="2"
                    fill="#9050d8"
                  />
                  <rect
                    x="22"
                    y="24"
                    width="10"
                    height="24"
                    rx="2"
                    fill="#a060e8"
                  />
                  <rect
                    x="36"
                    y="14"
                    width="10"
                    height="34"
                    rx="2"
                    fill="#b070f0"
                  />
                  <path
                    d="M13,36 Q27,20 41,10"
                    stroke="#d0a0ff"
                    stroke-width="1.8"
                    fill="none"
                  />
                  <circle
                    cx="27"
                    cy="27"
                    r="17"
                    fill="none"
                    stroke="#7a30b8"
                    stroke-width="2"
                    opacity="0.5"
                  />
                  <line
                    x1="40"
                    y1="40"
                    x2="48"
                    y2="48"
                    stroke="#7a30b8"
                    stroke-width="3.5"
                    stroke-linecap="round"
                  />
                </svg>
              </div>
              <div class="format-tile-label">
                PS Data
              </div>
              <div class="format-tile-sub">
                Analytics
              </div>
            </div>
            <div class="format-tile proj">
              <div class="format-tile-icon">
                <svg
                  width="54"
                  height="54"
                  viewBox="0 0 54 54"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="4"
                    y="22"
                    width="46"
                    height="30"
                    rx="4"
                    fill="#c09000"
                  />
                  <rect
                    x="4"
                    y="16"
                    width="20"
                    height="10"
                    rx="3"
                    fill="#d0a800"
                  />
                  <line
                    x1="12"
                    y1="34"
                    x2="38"
                    y2="34"
                    stroke="#1a1200"
                    stroke-width="2"
                  />
                  <line
                    x1="12"
                    y1="42"
                    x2="38"
                    y2="42"
                    stroke="#1a1200"
                    stroke-width="2"
                  />
                  <line
                    x1="12"
                    y1="38"
                    x2="30"
                    y2="38"
                    stroke="#1a1200"
                    stroke-width="2"
                  />
                  <circle
                    cx="42"
                    cy="16"
                    r="8"
                    fill="#e8b800"
                  />
                  <line
                    x1="42"
                    y1="11"
                    x2="42"
                    y2="21"
                    stroke="#7a5c00"
                    stroke-width="2.5"
                    stroke-linecap="round"
                  />
                  <line
                    x1="37"
                    y1="16"
                    x2="47"
                    y2="16"
                    stroke="#7a5c00"
                    stroke-width="2.5"
                    stroke-linecap="round"
                  />
                </svg>
              </div>
              <div class="format-tile-label">
                Project
              </div>
              <div class="format-tile-sub">
                Add / Open Files
              </div>
            </div>
            <div class="format-tile conc">
              <div class="format-tile-icon">
                <svg
                  width="54"
                  height="54"
                  viewBox="0 0 54 54"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <ellipse
                    cx="27"
                    cy="16"
                    rx="18"
                    ry="6"
                    fill="#0a7080"
                  />
                  <rect
                    x="9"
                    y="16"
                    width="36"
                    height="18"
                    fill="#085a68"
                  />
                  <ellipse
                    cx="27"
                    cy="34"
                    rx="18"
                    ry="6"
                    fill="#0a7080"
                  />
                  <ellipse
                    cx="27"
                    cy="24"
                    rx="18"
                    ry="6"
                    fill="#0c8090"
                  />
                  <text
                    x="27"
                    y="28"
                    text-anchor="middle"
                    font-size="12"
                    font-weight="700"
                    fill="#80e8f8"
                    font-family="sans-serif"
                  >C&deg;</text>
                </svg>
              </div>
              <div class="format-tile-label">
                Conc.
              </div>
              <div class="format-tile-sub">
                Concentration Data
              </div>
            </div>
          </div>
        </div>
        <div class="w-full lg:w-1/2 md:text-left lg:text-right pb-5 my-auto relative">
          <span class="inline-block py-px px-2 mb-4 text-xs leading-5 text-green-500 bg-green-100 font-medium uppercase rounded-full shadow-sm">
            Flexibility
          </span>
          <div class="md:w-full lg:w-3/4 lg:ml-auto">
            <h3 class="text-3xl md:text-4xl leading-tight text-coolGray-900 tracking-tighter font-bold">
              Import raw data from multiple formats seamlessly
            </h3>
            <p class="pt-4">
              Easily import raw data from various formats. Our platform ensures compatibility and flexibility, making data integration smooth and efficient
            </p>
          </div>
        </div>
      </div>
      <div
        id="connectivity-section"
        class="flex flex-wrap justify-center p-4 features-section-card"
      >
        <div class="w-full lg:w-1/2 text-left pb-5 my-auto relative">
          <span class="inline-block py-px px-2 mb-4 text-xs leading-5 text-green-500 bg-green-100 font-medium uppercase rounded-full shadow-sm">
            Connectivity
          </span>
          <div class="md:w-full lg:w-3/4">
            <h3 class="text-3xl md:text-4xl leading-tight text-coolGray-900 tracking-tighter font-bold">
              Effortlessly connect external applications and IoT devices
            </h3>
            <p class="pt-4">
              Integrate seamlessly with external applications and IoT devices. Our robust API ensures real-time data flow and enhanced functionality.
            </p>
          </div>
        </div>
        <div class="w-full lg:w-1/2 pb-5">
          <img
            src="../assets/Images/APIConnectivity.png"
            class="ml-auto"
            alt="..."
          >
        </div>
      </div>
      <div class="flex flex-wrap justify-center p-4 features-section-card">
        <div class="w-full lg:w-1/2 pb-5">
          <img
            src="../assets/Images/collaboration-infra.png"
            class=""
            alt="..."
          >
        </div>
        <div class="w-full lg:w-1/2 md:text-left lg:text-right pb-5 my-auto relative">
          <span class="inline-block py-px px-2 mb-4 text-xs leading-5 text-green-500 bg-green-100 font-medium uppercase rounded-full shadow-sm">
            Harmony
          </span>
          <div class="md:w-full lg:w-3/4 lg:ml-auto">
            <h3 class="text-3xl md:text-4xl leading-tight text-coolGray-900 tracking-tighter font-bold">
              Seamless Collaboration and Easy Sharing
            </h3>
            <p class="pt-4">
              Collaborate in real-time, accessing and sharing data effortlessly from anywhere. Keep all information centralized and up-to-date.
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="container p-4 section-faq hover:shadow-xl">
      <div class="md:max-w-4xl mx-auto pb-1 text-center">
        <h1 class="my-2 text-3xl md:text-4xl leading-tight text-coolGray-900 font-bold tracking-tighter">
          FAQs: Using <span class="header-quotes-effect font-bold">djuli</span> for your analysis
        </h1>
      </div>
      <div class="line-faq mb-7" />
      <div
        v-for="(faq, index) in faqs"
        :key="index"
        class="faq-item my-3 mx-2"
      >
        <button
          class="faq-question"
          @click="toggleFaq(index)"
        >
          {{ faq.question }}
        </button>
        <transition name="fade-slide">
          <div
            v-show="faq.active"
            :class="['faq-answer p-2', { show: faq.active }]"
          >
            {{ faq.answer }}
          </div>
        </transition>
      </div>
    </div>
        
    <!-- New Contact Section -->
    <div class="container p-8 mx-auto text-center contact-section">
      <h3 class="mb-4 text-2xl md:text-2xl leading-tight text-coolGray-900 font-bold tracking-tighter">
        Still Have Questions? <span class="header-quotes-effect">Contact Us!</span>
      </h3>
      <p class="mb-6 text-lg text-coolGray-500">
        Our team is here to help you get started with your data analysis journey
      </p>
      <a
        href="https://www.zimmerpeacocktech.com/contact-support/" 
        class="inline-block px-4 py-2 text-lg font-medium text-white bg-green-500 hover:bg-green-600 rounded-md transition duration-200"
      >
        Get in Touch
      </a>
    </div>
  </div>
</template>

<script>
    export default {
        name: 'Home',
        metaInfo: {
            title: 'Djuli – Cloud Sensor Data Analysis Platform',
            meta: [
                { name: 'description', content: 'Advanced cloud-based tools for sensor data analysis. Import IoT data, run electrochemical analysis, generate professional reports. Try free.' },
                { property: 'og:title', content: 'Djuli – Cloud Sensor Data Analysis Platform' },
                { property: 'og:description', content: 'Advanced cloud-based tools for sensor data analysis. Import IoT data, run electrochemical analysis, generate professional reports.' },
                { property: 'og:url', content: 'https://dju.li/home' },
                { property: 'og:type', content: 'website' },
            ],
        },
        data() {
            return {
                scrollY: 0,
                quotesVisible: false,
                formatsVisible: false,
                djuliReportGifVisible:false,
                djuliPreprocessingGifVisible:false,
                faqs: [
                    {
                        question: 'What makes Djuli’s cloud analysis tool unique?',
                        answer: 'Djuli stands out for its seamless IoT device integration, advanced cloud-based analysis tools, and intuitive user interface. Our platform provides unmatched scalability, security, and collaboration features, making it perfect for comprehensive chemical data analysis and sensor-driven insights.',
                        active: true
                    },
                    {
                        question: 'How do I connect IoT devices and third-party apps to Djuli?',
                        answer: 'Djuli’s API allows you to easily connect IoT devices, sensors, and third-party apps for secure, flexible data exchange. You can send and retrieve real-time data, integrating various sources directly into your analysis for comprehensive insights',
                        active: false
                    },
                    {
                        question: 'What data formats can I import into Djuli?',
                        answer: `Djuli supports a wide variety of data formats, including:
- CSV
- XLSX
- JSON
- XML
- PSSession
- MTD
- TRM
- DT6
- TXT
Additionally, you can import media files such as images, making it easy to integrate visual data. This broad compatibility ensures seamless integration with your existing data sources`,
                        active: false
                    },
                    {
                        question: 'What predefined studies and experiments does Djuli offer?',
                        answer: `Djuli offers a wide range of predefined studies for your experiments, including:
*Discrete:*
- Amperometry
- Potentiometry
- Voltammetry
- Impedance
- Spatial mapping
*Continuous:*
- Potentiometry
- Amperometry
You also have the option to create a Generic study tailored to your specific experiments, offering maximum flexibility for your analytical needs.`,
                        active: false
                    },
                    {
                        question: 'How does djuli ensure the security of my data?',
                        answer: 'Djuli prioritizes data security by using advanced encryption protocols and secure cloud infrastructure. Your data is protected at every stage, from storage to transfer, ensuring complete confidentiality and security compliance.',
                        active: false
                    },
                    {
                        question: 'How can I collaborate with my team using djuli?',
                        answer: 'Djuli makes team collaboration easy by allowing you to share report clusters, assign reviewers, and create custom templates. This ensures all team members have access to consistent and up-to-date information. Djuli’s collaborative features support efficient workflows, helping your team stay organized and productive.',
                        active: false
                    },
                    {
                        question: 'What preprocessing tools are available on the platform?',
                        answer: `Djuli offers a powerful suite of data preprocessing tools, including:
* Crop and Baseline Adjustment
* Filters: BPF, BSF, HPF, LPF, Median Filter Moving Average and Savitzky-Golay Smoothing
* Data Manipulation: Flip, Offset, Extrapolate, Accumulate, Differentiate
* Outlier Detection
* These tools help you prepare your data for deeper analysis and ensure high-quality results`,
                        active: false
                    },
                    {
                        question: 'What data analysis tools does djuli provide?',
                        answer: `Djuli provides a comprehensive set of data analysis tools, including:
* Equation Fitting
* Statistical Data Extraction (General and per line)
* Standard Addition
* Feature Check
* Delta and Mean Fit
* Peak Fit Analysis (Position, Height, and Area Under Curve)
* Spatial Mapping
These tools allow for precise, detailed analysis, enabling you to extract valuable insights from your data.`,
                        active: false
                    },
                    {
                        question: 'Is there a limit to how much data I can store in Djuli’s cloud?',
                        answer: 'Djuli offers scalable cloud storage with no hard limits on data. Our flexible plans are designed to grow with your needs, ensuring you have the capacity to store and analyze as much data as required, no matter the size of your projects.',
                        active: false
                    },
                    {
                        question: 'How does Djuli help generate professional reports from my data analysis?',
                        answer: 'Djuli makes professional report generation effortless. After uploading your raw data and selecting your analysis, the system automatically creates a structured report for you. You can then easily fill in any missing details, customize the layout to your needs, and export it as a PDF or share it directly with clients. With just a few clicks, you can create polished reports that save you time and effort.".',
                        active: false
                    },
                    {
                        question: 'What tutorials or support documentation does Djuli offer?',
                        answer: 'Djuli offers comprehensive support through detailed tutorials and extensive documentation. Our user-friendly Context Help feature displays relevant guides and tips directly within the platform, triggered by what you hover over. For deeper learning, we also provide a full online course to help you maximize the platform’s potential and optimize your workflows.',
                        active: false
                    },
                ]
            };
        },
        computed: {
            divStyle() {
                return {
                    transform: this.scrollY < 80 ? `translateY(${160 - (this.scrollY * 2)}px)` : 'translateY(0)',
                    opacity: this.scrollY > 10 ? 1 : 0
                };
            },
            faqSchema() {
                return {
                    '@context': 'https://schema.org',
                    '@type': 'FAQPage',
                    'mainEntity': this.faqs.map(faq => ({
                        '@type': 'Question',
                        'name': faq.question,
                        'acceptedAnswer': {
                            '@type': 'Answer',
                            'text': faq.answer,
                        },
                    })),
                };
            },
        },
        mounted() {
            window.addEventListener('scroll', this.handleScroll);
            this.createObserver();
            document.dispatchEvent(new Event('render-event'));
            // Insert FAQ schema as JSON-LD into the head
            const script = document.createElement('script');
            script.setAttribute('type', 'application/ld+json');
            script.textContent = JSON.stringify(this.faqSchema);
            document.head.appendChild(script);
        },
        beforeDestroy() { // eslint-disable-line vue/no-deprecated-destroyed-lifecycle
            window.removeEventListener('scroll', this.handleScroll);
        },
        methods: {
            handleScroll() {
                this.scrollY = window.scrollY;
            },
            createObserver() {
                const options = {
                    threshold: 0.75
                };

                const observerReport = new IntersectionObserver(this.handleIntersectionReport, options);
                observerReport.observe(this.$refs.djuliReportGifContainer);

                const observerPreprocessing = new IntersectionObserver(this.handleIntersectionPreprocessing, options);
                observerPreprocessing.observe(this.$refs.djuliPreprocessingGifContainer);

                const checkQuotes = () => {
                    if (!this.$refs.quotesSection) return;
                    const rect = this.$refs.quotesSection.getBoundingClientRect();
                    if (rect.top < window.innerHeight && rect.bottom > 0) {
                        this.quotesVisible = true;
                        window.removeEventListener('scroll', checkQuotes);
                    }
                };
                window.addEventListener('scroll', checkQuotes);
                this.$nextTick(checkQuotes);

                const checkFormats = () => {
                    if (!this.$refs.formatsSection) return;
                    const rect = this.$refs.formatsSection.getBoundingClientRect();
                    if (rect.top < window.innerHeight * 0.85 && rect.bottom > 0) {
                        this.formatsVisible = true;
                        window.removeEventListener('scroll', checkFormats);
                    }
                };
                window.addEventListener('scroll', checkFormats);
                this.$nextTick(checkFormats);
            },
            handleIntersectionReport(entries) {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        // When element is in view, wait for 2 seconds
                        setTimeout(() => {
                            this.djuliReportGifVisible = true; // Show the GIF after delay
                        }, 2000);
                    }
                });
            },
            handleIntersectionPreprocessing(entries) {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        this.djuliPreprocessingGifVisible = true;
                    }
                });
            },
            toggleFaq(index) {
                this.faqs[index].active = !this.faqs[index].active;
            },
            scrollToSection(sectionId) {
                const section = document.getElementById(sectionId);
                if (section) {
                    section.scrollIntoView({ behavior: 'smooth' });
                }
            },
        },
    }
</script>

<style lang="scss" scoped>
.hero-section{
    height: 78vh;
}
.section-features{
    background-color: #fefefe;
}
.scroll-div {
    transition: transform 1s ease-in-out, opacity 1s ease-in-out;
    position: relative;
  }
.line-header-quote {
    width: 200px; /* Define the width of the line */
    height: 3px;   /* Define the height (thickness) of the line */
    background-color: grey; /* Set the line color */
    margin: 0 auto; /* This will center the line */
}
.header-quotes-effect{
    color:adjust-color($color: #26bc5e);
    transition: color 0.3s ease;
}
.header-quotes-effect:hover{
    color:adjust-color($color: #2bdd6c)
}
.quote-card {
    position: relative;
    padding: 2.5rem 2rem 2rem;
    border-radius: 0.75rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    min-height: 240px;
    height: 100%;
    opacity: 0;
    transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}
.quote-from-left   { transform: translateX(-65px); }
.quote-from-bottom { transform: translateY(50px); }
.quote-from-right  { transform: translateX(65px); }
.quote-card::before {
    content: '\201C';
    position: absolute;
    top: -0.25rem;
    left: 1.25rem;
    font-size: 7rem;
    line-height: 1;
    color: #26bc5e;
    font-family: Georgia, serif;
    opacity: 0.25;
    pointer-events: none;
}
.quote-card--white { background: #ffffff; }
.quote-card--green { background: #8dd8a9; }
.quote-text {
    font-size: 1.4rem;
    font-style: italic;
    font-weight: 600;
    color: #1f2937;
    line-height: 1.65;
    margin: 0;
    flex: 1;
    padding-top: 1.75rem;
    text-align: left;
}
.quote-footer {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 2px solid rgba(38, 188, 94, 0.35);
}
.quote-dash {
    color: #26bc5e;
    font-size: 1.25rem;
    font-weight: 700;
}
.quote-author {
    font-weight: 700;
    color: #374151;
    font-size: 0.95rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}
.quote-delay-1 { transition-delay: 0.15s; }
.quote-delay-2 { transition-delay: 0.30s; }
.quote-card.quote-visible {
    opacity: 1;
    transform: translate(0, 0);
}
.line-faq{
    width: 25vw;
    height: 2px;
    background-color: #26bc5e;
    margin: 0 auto;
}
.section-faq{
    background-color: #fdfdfd;
}
.section-quotes{
    background-color: #f5f5f5;
    padding-bottom: 1rem;
}
.features-section-card img{
    max-height: 550px;
    width: auto;
}
.faq-item {
    margin-bottom: 20px;
}
.faq-question {
    background-color: #8dd8a9;
    padding: 10px;
    border: none;
    width: 100%;
    text-align: left;
    cursor: pointer;
    font-size: 18px;
    font-weight: bold;
}
.faq-question:hover {
    background-color: #85c79c;
}
.faq-answer {
    padding: 10px;
    background-color: #fff;
    border: 1px solid #8dd8a9;
    opacity: 0; /* Start hidden */
    transform: translateY(-10px); /* Slide up */
    transition: opacity 0.75s ease, transform 0.75s ease; /* Smooth transitions */
    pointer-events: none; /* Prevents interaction when hidden */
}

.fade-slide-enter-active, .fade-slide-leave-active {
    transition: opacity 0.75s ease, transform 0.75s ease; /* Transition properties */
}

.fade-slide-enter, .fade-slide-leave-to /* .fade-slide-leave-active in <2.1.8 */ {
    opacity: 0; /* Start from transparent */
    transform: translateY(-10px); /* Slide up */
}

/* Make the text visible when active */
.faq-answer.show {
    opacity: 1; /* Fully visible when active */
    transform: translateY(0); /* Move back to original position */
    pointer-events: auto; /* Allow interaction */
}

.contact-section {
    background-color: #fdfdfd;
    margin-top: 2rem;
    padding-bottom: 4rem;
}

/* File formats grid */
.formats-grid {
    display: grid;
    grid-template-columns: repeat(4, 110px);
    grid-template-rows: repeat(3, 110px);
    gap: 10px;
    padding: 16px;
}
.format-tile {
    width: 110px;
    height: 110px;
    border-radius: 14px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    opacity: 0;
    transform: scale(0.7) translateY(20px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    position: relative;
    overflow: hidden;
    cursor: default;
    transition: transform 0.2s, box-shadow 0.2s;
}
.formats-visible .format-tile {
    animation: ftPopIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards,
               ftRing 1.4s ease-out 1s 1;
}
.format-tile:hover {
    transform: scale(1.07) translateY(-4px) !important;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16);
    z-index: 10;
}
.format-tile::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.13);
    opacity: 0;
    transition: opacity 0.2s;
}
.format-tile:hover::after { opacity: 1; }

@keyframes ftPopIn {
    to { opacity: 1; transform: scale(1) translateY(0); }
}
@keyframes ftRing {
    0%   { box-shadow: 0 0 0 0 rgba(100, 100, 200, 0.3); }
    70%  { box-shadow: 0 0 0 14px rgba(100, 100, 200, 0); }
    100% { box-shadow: 0 0 0 0 rgba(100, 100, 200, 0); }
}
@keyframes ftFloat {
    0%, 100% { transform: translateY(0px); }
    50%       { transform: translateY(-5px); }
}

.formats-visible .format-tile:nth-child(1)  { animation-delay: 0.10s; }
.formats-visible .format-tile:nth-child(2)  { animation-delay: 0.45s; }
.formats-visible .format-tile:nth-child(3)  { animation-delay: 0.05s; }
.formats-visible .format-tile:nth-child(4)  { animation-delay: 0.60s; }
.formats-visible .format-tile:nth-child(5)  { animation-delay: 0.25s; }
.formats-visible .format-tile:nth-child(6)  { animation-delay: 0.35s; }
.formats-visible .format-tile:nth-child(7)  { animation-delay: 0.15s; }
.formats-visible .format-tile:nth-child(8)  { animation-delay: 0.55s; }
.formats-visible .format-tile:nth-child(9)  { animation-delay: 0.40s; }
.formats-visible .format-tile:nth-child(10) { animation-delay: 0.20s; }
.formats-visible .format-tile:nth-child(11) { animation-delay: 0.50s; }
.formats-visible .format-tile:nth-child(12) { animation-delay: 0.30s; }

.format-tile svg { animation: ftFloat 3s ease-in-out infinite; }
.format-tile:nth-child(odd) svg  { animation-delay: 0.4s; }
.format-tile:nth-child(3n) svg   { animation-delay: 0.8s; }
.format-tile:nth-child(4n) svg   { animation-delay: 1.2s; }

.format-tile-icon { font-size: 0; line-height: 0; }
.format-tile-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}
.format-tile-sub {
    font-size: 8px;
    font-weight: 400;
    letter-spacing: 0.03em;
    opacity: 0.75;
    text-align: center;
    padding: 0 6px;
}

/* Tile color themes */
.format-tile.csv  { background: #e8f7ed; }
.format-tile.xml  { background: #fff8e6; }
.format-tile.fhtml { background: #fff1e6; }
.format-tile.xlsx { background: #e6f4ea; }
.format-tile.pss  { background: #e6f0fb; }
.format-tile.dt6  { background: #f0f0f0; }
.format-tile.fpng { background: #e8f0fe; }
.format-tile.txt  { background: #e6f7fb; }
.format-tile.aoi  { background: #edfaf0; }
.format-tile.ps   { background: #f3eaff; }
.format-tile.proj { background: #fffbe6; }
.format-tile.conc { background: #e6f9fb; }

.format-tile.csv  .format-tile-label, .format-tile.csv  .format-tile-sub  { color: #1a6b2f; }
.format-tile.xml  .format-tile-label, .format-tile.xml  .format-tile-sub  { color: #8b5e00; }
.format-tile.fhtml .format-tile-label, .format-tile.fhtml .format-tile-sub { color: #b03000; }
.format-tile.xlsx .format-tile-label, .format-tile.xlsx .format-tile-sub  { color: #1a5c28; }
.format-tile.pss  .format-tile-label, .format-tile.pss  .format-tile-sub  { color: #1a4a8a; }
.format-tile.dt6  .format-tile-label, .format-tile.dt6  .format-tile-sub  { color: #444; }
.format-tile.fpng .format-tile-label, .format-tile.fpng .format-tile-sub  { color: #1a3a8a; }
.format-tile.txt  .format-tile-label, .format-tile.txt  .format-tile-sub  { color: #0a5070; }
.format-tile.aoi  .format-tile-label, .format-tile.aoi  .format-tile-sub  { color: #1a5c28; }
.format-tile.ps   .format-tile-label, .format-tile.ps   .format-tile-sub  { color: #5a1a8a; }
.format-tile.proj .format-tile-label, .format-tile.proj .format-tile-sub  { color: #7a5c00; }
.format-tile.conc .format-tile-label, .format-tile.conc .format-tile-sub  { color: #006070; }
</style>